import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants } from "@/constants/tools.constant";
import ProductForm from "@/pages/tools/Products/components/ProductForm";
import { useMutateProductsMutation } from "@/redux/api/toolsQuery";
import {
  TAddProduct,
  createProductSchema,
  defaultProduct,
} from "@/schemas/tools.schema";
import {
  filterEmptyValues,
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface AddProductModalProps {
  isOpen: boolean;
  toggleAddProductModal: () => void;
}

const AddProductModal = ({
  isOpen,
  toggleAddProductModal,
}: AddProductModalProps): JSX.Element => {
  const {
    FORM: { PRODUCT },
  } = ToolsConstants;

  const [createProduct, { isLoading }] = useMutateProductsMutation();

  const form = useForm<TAddProduct>({
    resolver: zodResolver(createProductSchema),
    defaultValues: defaultProduct,
  });

  const onSubmit = async (data: TAddProduct): Promise<void> => {
    const response = await createProduct({
      method: "POST",
      data: parseToFormData(filterEmptyValues(data), ["images", "files"]),
    });

    validateResponse({
      response,
      onSuccess: () => {
        form.reset();
        toggleAddProductModal();
        toast.success(PRODUCT.ADD_PRODUCT_SUCCESS);
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      title={PRODUCT.ADD_PRODUCT}
      toggleModal={toggleAddProductModal}
      className='md:max-w-3xl '
      isFormDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form className=' space-y-8' onSubmit={form.handleSubmit(onSubmit)}>
          <ProductForm />
          <div className=' flex justify-between'>
            <Button
              variant={"ghost"}
              onClick={() => form.reset(defaultProduct)}
              disabled={isLoading}
            >
              {Button_Names.Reset}
            </Button>
            <Button type='submit' isLoading={isLoading}>
              {PRODUCT.SAVE_PRODUCT}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AddProductModal;
